/* voyado-slider.js
 * Unified Voyado recommendation carousel.
 * Algorithm and product context come from data attributes on <voyado-slider>
 * so multiple instances on the same page don't conflict.
 *
 * Rendering helpers, slider behaviour, and shared constants live in
 * voyado-recommendations-core.js (window.VoyadoRec).
 */

// The Elevate SDK (window.elevate) handles all request plumbing — cluster URL,
// market/locale/touchpoint, and customerKey/sessionKey (incl. the logged-in key
// via its session handler) are auto-attached to every query.* / notify.* call.

class VoyadoSlider extends HTMLElement {
  #grid = null;
  #ac = null;
  #slider = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#init(this.#ac.signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #init(signal) {
    const R = window.VoyadoRec;
    if (!R) { console.warn("[voyado-slider] VoyadoRec core missing", this); return; }

    await window.voyadoElevateReady;
    if (signal.aborted) return;

    if (!window.elevate) {
      console.warn("[voyado-slider] window.elevate is not ready — slider aborted", this);
      return;
    }

    const recommendationId = this.dataset.recommendationId ?? "";
    const algorithm = this.dataset.algorithm ?? "RECENTLY_VIEWED";
    const productKey = this.dataset.productKey ?? "";
    // Elevate page references omit Shopify's route prefix (/pages/dame -> /dame),
    // so derive it by stripping that prefix — matching voyado-collection.js. An
    // explicit data-page-reference (section setting) overrides this for edge cases.
    const pageReference =
      this.dataset.pageReference ||
      location.pathname.replace(/^\/(collections|pages)/, "");
    const isProductPage = !!productKey;

    // Hide silently if algorithm needs product context but we're not on a product page
    if (!recommendationId && !isProductPage && R.NEEDS_PRODUCT.has(algorithm)) {
      console.warn(
        `[voyado-slider] hidden: algorithm "${algorithm}" needs product context, but not a product page`,
        this
      );
      this.hidden = true;
      return;
    }

    this.#grid = this.querySelector("[data-voyado-grid]");
    if (!this.#grid) return;

    this.#slider = R.setupSlider(this.#grid, {
      prev: this.querySelector("[data-prev]"),
      next: this.querySelector("[data-next]"),
      scrollbarWrap: this.querySelector("[data-voyado-scrollbar]"),
      scrollbarThumb: this.querySelector("[data-voyado-scrollbar-thumb]"),
    }, signal);

    this.#fetch(algorithm, productKey, recommendationId, pageReference);
  }

  async #fetch(algorithm, productKey, recommendationId = "", pageReference = "/") {
    const R = window.VoyadoRec;
    if (!window.elevate?.query) {
      this.hidden = true;
      return;
    }
    try {
      const isProductPage = !!productKey;

      // The Elevate query keys each requested list by `id` — the listing-area
      // identifier configured on the page. There is no `recommendationId` field;
      // the merchant's Elevate ID IS the `id`. Sending a literal "recs" for every
      // slider made all landing-page sliders request the same list (and the API
      // dropped the unknown `recommendationId`), so they all rendered identically.
      const listId = recommendationId || "recs";
      const listEntry = recommendationId
        ? { id: listId }
        : { id: listId, algorithm };
      const body = { recommendationLists: [listEntry] };

      // market/locale/touchpoint/customerKey/sessionKey are auto-attached by the SDK.
      const data = isProductPage
        ? await window.elevate.query.productPage({ productKey }, body)
        : await window.elevate.query.landingPage({ pageReference }, body);

      if (isProductPage) R.captureVariantTickets(data);
      this.#render(data, listId);
    } catch (e) {
      console.error("[voyado-slider] fetch failed:", e, this);
      this.#removeSkeleton();
      this.hidden = true;
    }
  }

  #render(data, wantId = "") {
    const R = window.VoyadoRec;
    this.#removeSkeleton();
    // Match our requested list by id. A landing-page query may echo every area
    // configured on the page, so picking [0] would show the wrong (first) list.
    const lists = data.recommendationLists ?? [];
    const matched = wantId ? lists.find((l) => l.id === wantId) : null;
    const list = matched || lists[0];
    const groups = list?.productGroups ?? data.productGroups ?? [];

    if (!groups.length) {
      this.hidden = true;
      return;
    }
    const frag = document.createDocumentFragment();
    let i = 0;
    for (const g of groups) {
      const p = g.products?.[0] ?? g;
      const ticket = g.ticket ?? p.ticket ?? "";
      const item = document.createElement("div");
      item.className = "product-series__item";
      item.setAttribute("role", "listitem");
      item.innerHTML = R.renderCard(p, ticket, i++);
      frag.appendChild(item);
    }
    this.#grid.appendChild(frag);

    // Products loaded asynchronously after init — re-evaluate arrow + scrollbar
    // state now that the real cards (not skeletons) determine the track's scroll width.
    this.#slider?.update();
  }

  #removeSkeleton() {
    this.#grid
      ?.querySelectorAll(".voyado-skeleton")
      .forEach((el) => el.remove());
  }
}

customElements.define("voyado-slider", VoyadoSlider);
